import React, { Fragment, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ReactComponent as NoDataFoundImg } from '../../assets/images/no-data-found.svg';
import PropTypes from 'prop-types';
import {
  errorToast,
  getAccountType,
  getContentType,
  getFocusType,
  getMeditationType,
  getMultiBadge,
  getPathForSurvey,
  getSentToUser,
  getStatusBgColorPeapExpert,
  getUserType,
  successToast
} from '../../utils/helper';
import ProfilePic from '../../assets/images/dummy_profile.png';
import Thumbnail from '../../assets/images/thumbnail.png';
import { useLocation, useNavigate } from 'react-router-dom';
import DeletePopup from './modals/DeletePopup';
import {
  PencilSquareIcon,
  TrashIcon,
  ChevronUpDownIcon,
  ChevronDoubleUpIcon,
  ChevronDoubleDownIcon,
  ArrowDownTrayIcon,
  DocumentDuplicateIcon,
  BookmarkIcon as BookmarkOutlineIcon,
  ChevronRightIcon as ViewChevronIcon,
  EllipsisHorizontalIcon
} from '@heroicons/react/24/outline';
import moment from 'moment';
import AudioPlayer from '../AudioPlayer/AudioPlayer';
import VideoPlayer from './modals/VideoPlayer';
import BulkOperationPopup from './modals/BulkOperationPopup';
import { Api } from '../../api';
import ConfirmPopup from './modals/ConfirmPopup';
import ActiveInactiveToggle from './ActiveInactiveToggle';
import googleIcon from '../../assets/images/google.png';
import fbIcon from '../../assets/images/fb.png';
import appleIcon from '../../assets/images/apple.png';
import mailIcon from '../../assets/images/email.png';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import { CONTENT_APPROVAL_TYPE } from '../../utils/constants';
import { Listbox, Menu, Popover, Transition } from '@headlessui/react';
import Loader from './Loader';
import LazyLoadImageProp from './LazyLoadImage';
import FocusPopup from './modals/FocusPopup';
import ActiveInactiveCompanyToggle from './ActiveInactiveCompanyToggle';
import Select from './Select';
import { BookmarkIcon as BookmarkSolidIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import CommonModal from './modals/commonModal';

const Table = ({
  columns,
  data = [],
  setDeleteId,
  setSelectedRow,
  name,
  addNewURL,
  sortBy,
  setSortBy,
  sortOrder,
  refreshTable,
  contentType,
  loader,
  setSearchTerm,
  showIndex,
  onNameClick,
  isEdit = false,
  isdetail = true,
  isname = false,
  handleDropdownChange,
  options,
  minHeight,
  activeRowId,
  handleResetPassword,
  selectedWinners = [], // ✅ Optional
  toggleWinner = () => {},
  tbodyMaxHeight = ''
}) => {
  const checkbox = useRef({ indeterminate: false });
  const getColumnFreezeId = (column, index) =>
    column?.sortKey || column?.key || column?.title || `column-${index}`;
  const isColumnFreezable = (column) =>
    column?.freeze === true || column?.freeze === 'left' || column?.freeze === 'Left';
  const [frozenColumnsMap, setFrozenColumnsMap] = useState({});
  const [checked, setChecked] = useState(false);
  const [indeterminate, setIndeterminate] = useState(false);
  const [selectedData, setSelectedData] = useState([]);
  const [selectedRowOfTable, setSelectedRowOfTable] = useState(null);
  const [imageError, setImageError] = useState(false);
  const location = useLocation();

  const handleImageError = () => {
    setImageError(true);
  };

  const handleDownload = async (imageUrl) => {
    try {
      // Fetch the image as a blob
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error('Failed to fetch the image');
      }

      const blob = await response.blob();

      // Create a temporary URL for the blob
      const urlBlob = window.URL.createObjectURL(blob);

      // Create a link element to download the image
      const a = document.createElement('a');
      a.href = urlBlob;
      a.download = 'payment_receipt.png'; // Set the file name
      document.body.appendChild(a);
      a.click();

      // Cleanup
      document.body.removeChild(a);
      window.URL.revokeObjectURL(urlBlob);
    } catch (error) {
      console.error('Error downloading the image:', error);
    }
  };
  useLayoutEffect(() => {
    setFrozenColumnsMap((prevState) => {
      const nextState = {};

      columns?.forEach((column, index) => {
        if (!isColumnFreezable(column)) return;

        const freezeId = getColumnFreezeId(column, index);
        nextState[freezeId] = prevState[freezeId] ?? false;
      });

      return nextState;
    });
  }, [columns]);

  useLayoutEffect(() => {
    if (data?.length > 0) {
      const isIndeterminate = selectedData?.length > 0 && selectedData?.length < data?.length;
      setChecked(selectedData?.length === data?.length);
      setIndeterminate(isIndeterminate);
      checkbox.current.indeterminate = isIndeterminate;
    }
  }, [selectedData, data?.length]);

  function toggleAll() {
    setSelectedData(checked || indeterminate ? [] : data?.map((temp) => temp.id));
    setChecked(!checked && !indeterminate);
    setIndeterminate(false);
  }

  const toggleColumnFreeze = (column, index) => {
    const freezeId = getColumnFreezeId(column, index);
    setFrozenColumnsMap((prevState) => ({
      ...prevState,
      [freezeId]: !prevState[freezeId]
    }));
  };

  const frozenColumnMeta = useMemo(() => {
    const leftOffsets = {};
    const frozenIndexes = [];
    let leftAccumulator = 0;

    columns?.forEach((column, index) => {
      const freezeId = getColumnFreezeId(column, index);
      const isPinned = isColumnFreezable(column) && frozenColumnsMap[freezeId];
      const columnWidth = Number(column?.width || 250); // Default width if not specified
      const canStick = isPinned && Number.isFinite(columnWidth) && columnWidth > 0;
      if (!canStick) return;

      leftOffsets[index] = leftAccumulator;
      frozenIndexes.push(index);
      leftAccumulator += columnWidth;
    });

    const lastFrozenIndex = frozenIndexes[frozenIndexes.length - 1];

    return {
      leftOffsets,
      lastFrozenIndex,
      isFrozenIndex: (index) => Object.prototype.hasOwnProperty.call(leftOffsets, index)
    };
  }, [columns, frozenColumnsMap]);

  const getStickyStyle = (index, isHeader = false) => {
    const isFrozen = frozenColumnMeta.isFrozenIndex(index);

    if (isHeader) {
      if (!isFrozen) {
        // Non-frozen header: stick vertically only (when the table has a bounded height)
        return tbodyMaxHeight ? { position: 'sticky', top: 0, zIndex: 20 } : {};
      }
      // Frozen header (corner cell): stick on both axes independently — no nested-sticky conflict
      return {
        position: 'sticky',
        ...(tbodyMaxHeight ? { top: 0 } : {}),
        left: `${frozenColumnMeta.leftOffsets[index]}px`,
        zIndex: tbodyMaxHeight ? 30 : 20,
        opacity: 1,
        borderRight:
          index === frozenColumnMeta.lastFrozenIndex ? '1px solid rgba(226, 232, 240, 0.9)' : ''
      };
    }

    if (!isFrozen) return {};

    // Frozen body cell: stick horizontally only
    return {
      position: 'sticky',
      left: `${frozenColumnMeta.leftOffsets[index]}px`,
      zIndex: 10,
      opacity: 0.98,
      background: 'white',
      borderRight:
        index === frozenColumnMeta.lastFrozenIndex ? '1px solid rgba(226, 232, 240, 0.9)' : ''
    };
  };

  const [openDeletePopup, setDeletePopup] = useState(false);
  const [bulkOperationPopup, setBulkOperationPopup] = useState(false);
  const [openConfirmPopup, setOpenConfirmPopup] = useState(false);
  const [openResetPopup, setOpenResetPopup] = useState(false);
  const [openVideoPlayer, setVideoPlayer] = useState(false);
  const [idToDelete, setIdToDelete] = useState('');
  const [activePlaying, setActivePlaying] = useState(null);
  const [actionType] = useState('');
  const [open, setOpen] = React.useState(false);
  const [openFocusPopup, setOpenFocusPopup] = useState(false);
  const [focusData, setFocusData] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedExpertId, setSelectedExpertId] = useState(null);

  const navigate = useNavigate();

  const deleteHandler = (id) => {
    setIdToDelete(id);
    // console.log("id x144", id);
    setDeletePopup(true);
  };

  const activePlayerHandler = (id) => {
    setActivePlaying(id);
  };

  const pauseHandler = () => {
    setActivePlaying(null);
  };

  const focusPopUpHandler = (state) => {
    setFocusData([]);
    setOpenFocusPopup(true);
    setFocusData(state);
  };

  const formattedData = (rowData, d, type, column, index) => {
    const data = d;
    const handleOldNew = (state) => {
      navigate(column.isTwoOption, {
        state: {
          ...rowData,
          action: 'view',
          option: state?.value === 1 ? 1 : 2
        }
      });
    };

    const handleCloseDialog = () => {
      setIsDialogOpen(false);
      setSelectedExpertId(null);
    };

    const buildUrlWithId = (url, isDraft, rowData, isEdit = false) => {
      const id = rowData._id || rowData?.id;

      return isEdit
        ? `${url}?id=${id}${isDraft ? '&type=draft' : ''}`
        : `${url?.replace(':id', id)}`;
    };

    if (type === 'badge') {
      return (
        <p
          className={`px-6 py-1 text-xs inline-block capitalize rounded-full border text-center ${
            data === 1 || data === true
              ? 'bg-[#DDF2BF] border-[#438038] text-[#438038]'
              : 'bg-[#F9F2F5] border-[#BF3261] text-[#BF3261]'
          }`}
        >
          {data === 1 || data === true ? 'active' : 'inactive'}
        </p>
      );
    } else if (type === 'icon-image') {
      // Small square icon cell (e.g. habit icons); dash when no icon is set.
      return data && typeof data === 'string' ? (
        <img
          src={data}
          alt="icon"
          className="w-9 h-9 rounded-lg object-contain bg-gray-100 dark:bg-shoorah-darkBgColor p-1"
          onError={(e) => {
            e.target.style.visibility = 'hidden';
          }}
        />
      ) : (
        <span className="text-gray-400 text-xs">—</span>
      );
    } else if (type === 'intro_badge') {
      return (
        <p
          className={`inline-flex m-0 rounded-full ${
            data === true ? 'bg-green-200' : 'bg-red-200'
          } px-3 py-[3px] text-md leading-5 capitalize font-semibold tracking-normal ${
            data === true ? 'text-green-700' : 'text-red-700'
          }`}
        >
          {data === true ? 'introduced' : 'Not introduced'}
        </p>
      );
    } else if (type === 'companyName') {
      let companyName = rowData.company_name?.split(' ');

      return (
        <div className="flex items-center justify-center">
          <div className="flex-shrink-0">
            <div>
              {rowData?.company_logo && !rowData?.company_logo.includes('null') ? (
                <LazyLoadImageProp imageSrc={rowData?.company_logo && rowData?.company_logo} />
              ) : (
                <div className="bg-black dark:bg-shoorah-darkBgColor flex justify-center items-center h-10 w-10 rounded-full">
                  <span className="text-white">{companyName[0]?.charAt(0)}</span>
                  <span className="text-white">{companyName[1]?.charAt(0)}</span>
                </div>
              )}
            </div>
          </div>
          <div className="ml-4">
            {/* <div className="font-medium dark:text-white text-gray-900">{rowData.company_name}</div> */}
          </div>
        </div>
      );
    } else if (type === 'userType') {
      return getUserType(data);
    } else if (type === 'contentType') {
      return getContentType(data);
    } else if (type === 'multi-badge') {
      const handleClick = () => {
        if (data.completed === 0) {
          navigate('/survey');
        }
      };

      return <div onClick={handleClick}>{getMultiBadge(data || {})}</div>;
    } else if (type === 'contentStatus') {
      return (
        <p
          className={`px-6 py-1 text-xs inline-block capitalize rounded-full border text-center ${
            data === 1
              ? 'bg-[#DDF2BF] border-[#438038] text-[#438038]'
              : data == 2
              ? 'bg-[#F9F2F5] border-[#BF3261] text-[#BF3261]'
              : ''
          }`}
        >
          {data === 0 ? 'Draft' : data === 1 ? 'Approved' : 'Rejected'}
        </p>
      );
    } else if (type === 'focusType') {
      return getFocusType(data);
    } else if (type === 'accountType') {
      return getAccountType(data);
    } else if (type === 'meditationType') {
      return getMeditationType(data);
    } else if (type === 'sentToUser') {
      return getSentToUser(data);
    } else if (type === 'gender') {
      return <div>{data === 0 ? 'Female' : 'Male'}</div>;
    } else if (type === 'profile') {
      let username = rowData.name?.split(' ');
      return (
        <div
          className={`flex items-center my-auto min-w-[280px]${
            column?.isView ? ' cursor-pointer' : ''
          }`}
          onClick={
            column?.isView
              ? () =>
                  navigate(buildUrlWithId(column.isView, false, rowData), {
                    state: { ...rowData, action: 'view' }
                  })
              : undefined
          }
        >
          <div className="flex-shrink-0">
            {/* Fixed condition to check for null, undefined, empty string, and 'null' string */}
            {data && data !== null && data !== '' && data !== 'null' && !data.includes('null') ? (
              <LazyLoadImageProp imageSrc={data || ProfilePic} onError={handleImageError} />
            ) : (
              <div className="self-center dark:bg-shoorah-darkBgColor bg-black flex justify-center items-center w-10 aspect-square rounded-full">
                <span className="text-white font-medium">
                  {username[0]?.charAt(0)?.toUpperCase()}
                </span>
                <span className="text-white font-medium">
                  {username[1]?.charAt(0)?.toUpperCase()}
                </span>
              </div>
            )}
          </div>

          {isdetail && !isname && (
            <div className="ml-4 flex-1 min-w-0">
              <div
                className={`font-medium dark:text-white text-gray-900 truncate${
                  column?.isView ? ' hover:text-blue-700 hover:underline' : ''
                }`}
              >
                {rowData?.name}
              </div>
              <div
                className="text-gray-500 lowercase text-sm cursor-help"
                data-tooltip-id={`profile-email-${rowData.id || index}`}
                style={{
                  wordBreak: 'break-all',
                  lineHeight: '1.2',
                  maxHeight: '2.4em',
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical'
                }}
              >
                {rowData?.email}
              </div>
              {rowData?.email && rowData?.email.length > 25 && (
                <ReactTooltip
                  id={`profile-email-${rowData.id || index}`}
                  place="bottom"
                  content={rowData?.email}
                  className="max-w-[300px] bg-gray-800 text-white text-xs z-50"
                />
              )}
            </div>
          )}
        </div>
      );
    } else if (type === 'partner-profile') {
      let companyName = rowData?.name || rowData?.companyName || rowData?.company_name || '';
      companyName = typeof companyName === 'string' ? companyName : '';
      let companyWords = companyName.split(' ');

      return (
        <div className="flex items-center my-auto min-w-[280px] max-w-[400px]">
          <div className="flex-shrink-0">
            {data !== null && data !== '' ? (
              <LazyLoadImageProp imageSrc={data || ProfilePic} onError={handleImageError} />
            ) : (
              <div className="self-center dark:bg-shoorah-darkBgColor bg-black flex justify-center items-center w-10 aspect-square rounded-full">
                <span className="text-white font-medium">
                  {companyWords[0]?.charAt(0)?.toUpperCase()}
                </span>
                <span className="text-white font-medium">
                  {companyWords[1]?.charAt(0)?.toUpperCase() ||
                    companyWords[0]?.charAt(1)?.toUpperCase()}
                </span>
              </div>
            )}
          </div>

          <div className="ml-4 flex-1 min-w-0">
            <div className="font-medium dark:text-white text-gray-900 truncate">{companyName}</div>
            <div
              className="text-gray-500 lowercase text-sm cursor-help"
              data-tooltip-id={`partner-email-${rowData.id || index}`}
              style={{
                wordBreak: 'break-all',
                lineHeight: '1.2',
                maxHeight: '2.4em',
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical'
              }}
            >
              {rowData?.email}
            </div>
            {rowData?.email && rowData?.email.length > 25 && (
              <ReactTooltip
                id={`partner-email-${rowData.id || index}`}
                place="bottom"
                content={rowData?.email}
                className="max-w-[300px] bg-gray-800 text-white text-xs z-50"
              />
            )}
          </div>
        </div>
      );
    } else if (type === 'profile-without-name') {
      // Get the name from the rowData - try multiple sources
      let usernameSource =
        rowData?.name || rowData?.offerTitle || rowData?.title || '' || rowData?.display_name;

      // Ensure it's a string and handle edge cases
      if (typeof usernameSource !== 'string') {
        usernameSource = '';
      }

      // Split for initials
      let username = usernameSource.trim().split(' ');

      // Debug logs (remove these after testing)
      // console.log('Profile Debug:', {
      //   rowData: rowData,
      //   data: data,
      //   usernameSource: usernameSource,
      //   username: username
      // });

      return (
        <div className="flex items-center my-auto min-w-[280px]">
          <div className="flex-shrink-0">
            {/* Enhanced image validation */}
            {data &&
            data !== null &&
            data !== '' &&
            data !== 'null' &&
            data !== 'undefined' &&
            typeof data === 'string' &&
            !data.includes('null') ? (
              <LazyLoadImageProp
                imageSrc={data}
                onError={handleImageError}
                className="w-10 h-10 rounded-full object-cover"
              />
            ) : (
              <div className="self-center dark:bg-shoorah-darkBgColor bg-black flex justify-center items-center w-10 h-10 rounded-full">
                <span className="text-white font-medium text-sm">
                  {username[0]?.charAt(0)?.toUpperCase() || 'O'}
                </span>
                <span className="text-white font-medium text-sm">
                  {username[1]?.charAt(0)?.toUpperCase() || 'F'}
                </span>
              </div>
            )}
          </div>

          {/* Always show the offer name/title */}
          <div className="ml-4 flex-1 min-w-0">
            <div className="font-medium dark:text-white text-gray-900 truncate">
              {usernameSource || 'Untitled Offer'}
            </div>
          </div>
        </div>
      );
    } else if (type === 'profile-without-name-2') {
      let usernameSource = '';

      let ThumbLetter = rowData?.name?.props?.children?.props?.children;

      usernameSource = typeof usernameSource === 'string' ? usernameSource : '';

      // Default to a single letter if string is empty
      // if (usernameSource === '') {
      //   usernameSource = 'User';
      // }

      // BRUT FORCED THE SOLUTION FOR NOW, WILL HAVE TO COME UP WITH A BETTER APPROACH LATER -

      let username = ThumbLetter.split(' ');

      return (
        <div className="flex items-center my-auto w-10">
          <div className="flex-shrink-0">
            {data !== null ? (
              <LazyLoadImageProp imageSrc={data || ProfilePic} onError={handleImageError} />
            ) : (
              <div className="self-center dark:bg-shoorah-darkBgColor bg-black flex justify-center items-center w-10 aspect-square rounded-full">
                <span className="text-white font-medium">
                  {username[0]?.charAt(0)?.toUpperCase() !== '['
                    ? username[0]?.charAt(0)?.toUpperCase()
                    : usernameSource?.charAt(0)?.toUpperCase()}
                </span>
                <span className="text-white font-medium">
                  {username[1]?.charAt(0)?.toUpperCase()}
                </span>
              </div>
            )}
          </div>
        </div>
      );
    } else if (type === 'profile-for-svgs') {
      let usernameSource =
        rowData?.name || rowData?.companyTitle || rowData?.companyName || rowData?.offerTitle || '';
      usernameSource = typeof usernameSource === 'string' ? usernameSource : '';
      let username = usernameSource.split(' ');
      console.log('m data', data);

      return (
        <div className="flex items-center my-auto w-10">
          <div className="flex-shrink-0">
            {data ? (
              // Check if data is a string URL for an SVG
              typeof data === 'string' && data.includes('.svg') ? (
                <img src={data} alt="SVG Icon" className="w-10 h-10" />
              ) : (
                // If data isn't a string or doesn't contain .svg, display a placeholder
                <div className="self-center dark:bg-shoorah-darkBgColor bg-black flex justify-center items-center w-10 aspect-square rounded-full">
                  <span className="text-white font-medium">
                    {username[0]?.charAt(0)?.toUpperCase()}
                  </span>
                  <span className="text-white font-medium">
                    {username[1]?.charAt(0)?.toUpperCase()}
                  </span>
                </div>
              )
            ) : (
              <div className="self-center dark:bg-shoorah-darkBgColor bg-black flex justify-center items-center w-10 aspect-square rounded-full">
                <span className="text-white font-medium">
                  {username[0]?.charAt(0)?.toUpperCase()}
                </span>
                <span className="text-white font-medium">
                  {username[1]?.charAt(0)?.toUpperCase()}
                </span>
              </div>
            )}
          </div>
          {isdetail && !isname && (
            <div className="ml-4 max-w-[250px]">
              <div className="font-medium dark:text-white text-gray-900">{rowData?.name}</div>
            </div>
          )}
        </div>
      );
    } else if (type === 'componyName') {
      return (
        <>
          {isname && column?.isView && (
            <div className="ml-4">
              <div
                //  style={{color:'blue'}}
                className=" hover:text-blue-700 underline"
                onClick={() =>
                  navigate(column?.isView, {
                    state: {
                      ...rowData,
                      action: 'edit'
                      // parantName:parant,parantURL:parantURL
                    }
                  })
                }
              >
                {rowData.name}
              </div>
              {/* <div className="text-gray-500 lowercase">{rowData?.email}</div> */}
            </div>
          )}
        </>
      );
    } else if (type === 'action') {
      const isSelected = selectedWinners?.includes(rowData?._id);
      return (
        <div className="flex items-center justify-end">
          {/*
            A row menu, for lists whose per-row actions include a destructive
            one. Loose icons put "delete" a few pixels from "edit" on a row you
            are mostly scanning; behind a menu it takes a deliberate second
            click, and the actions get names rather than glyphs.

            `rowMenu` is a function of the row so a screen can vary the items
            per record — returning [] renders nothing at all.
          */}
          {typeof column.rowMenu === 'function' &&
            (() => {
              const items = column.rowMenu(rowData) || [];
              if (!items.length) return null;
              return (
                <Menu as="div" className="relative">
                  <Menu.Button
                    className="p-1 rounded text-shoorah-lightBlack dark:text-white hover:bg-shoorah-normanGray dark:hover:bg-shoorah-darkBgColor"
                    aria-label="Row actions"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <EllipsisHorizontalIcon className="w-5 h-5" />
                  </Menu.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="opacity-0 scale-95"
                    enterTo="opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="opacity-100 scale-100"
                    leaveTo="opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 z-30 mt-1 w-48 origin-top-right rounded-btn bg-white dark:bg-shoorah-darkBgTabColor border-[1.5px] border-shoorah-borderGray dark:border-shoorah-darkBorder shadow-lg focus:outline-none divide-y divide-shoorah-borderGray dark:divide-shoorah-darkBorder overflow-hidden">
                      {items.map((item) => (
                        <Menu.Item key={item.label}>
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              item.onClick(rowData);
                            }}
                            className={`block w-full text-left px-4 py-3 text-sm hover:bg-shoorah-normanGray dark:hover:bg-shoorah-darkBgColor ${
                              item.danger
                                ? 'text-[#C0392B] dark:text-[#E88A80]'
                                : 'text-shoorah-lightBlack dark:text-white'
                            }`}
                          >
                            {item.label}
                          </button>
                        </Menu.Item>
                      ))}
                    </Menu.Items>
                  </Transition>
                </Menu>
              );
            })()}
          {column.isReset && (
            <div
              className="text-shoorah-secondary dark:text-white cursor-pointer"
              onClick={() => {
                setSelectedRowOfTable(rowData);
                setOpenResetPopup(true);
              }}
            >
              Reset Password
            </div>
          )}
          {column.selectButton && (
            <button
              onClick={() => toggleWinner(rowData?._id)}
              type="button"
              className={`text-sm px-5 h-[40px] rounded-full border border-[#0B1D2E] ${
                isSelected ? '!bg-[#95e624]' : ''
              }   text-[#0B1D2E]`}
            >
              {/* {isSelected ? 'Unselect' : 'Select as Winner'} */}
              {rowData?.status === 'winner' ? 'Selected Winner' : 'Select as Winner'}
            </button>
          )}

          {(column.isNavigate || column.arrowClickFun) && (
            <ChevronRightIcon
              className="w-[20px] h-[20px] ml-2 text-[#0B0B0B] dark:text-white cursor-pointer"
              onClick={() => {
                if (column.arrowClickFun) {
                  column.arrowClickFun(rowData);
                } else {
                  navigate(
                    buildUrlWithId(`${column.navigateUrl}/${rowData?._id}`, column.isDraft),
                    {
                      state: { ...rowData, action: 'view' }
                    }
                  );
                }
              }}
            />
          )}
          {column.isEdit && (
            <PencilSquareIcon
              className="w-[20px] ml-2 text-shoorah-secondary dark:text-white cursor-pointer"
              onClick={() => {
                if (column.handleEdit) {
                  column.handleEdit(rowData);
                } else {
                  navigate(buildUrlWithId(column.isEdit, column.isDraft, rowData), {
                    state: { ...rowData, action: 'edit' }
                  });
                }
              }}
            />
          )}
          {column.isClone && (
            <DocumentDuplicateIcon className="w-[20px] ml-2 text-shoorah-secondary dark:text-white cursor-pointer" />
          )}
          {column?.isView && (
            <ViewChevronIcon
              className="w-[20px] ml-2 text-shoorah-secondary dark:text-white cursor-pointer"
              onClick={() => {
                if (column.handleView) {
                  column.handleView(rowData);
                } else {
                  navigate(buildUrlWithId(column.isView, false, rowData), {
                    state: { ...rowData, action: 'view' }
                  });
                }
              }}
            />
          )}
          {/* For Survey we don'p want enter in this condition */}
          {column?.isTwoOption &&
            (rowData.parentId ? (
              <div>
                <ViewChevronIcon
                  className="w-[20px] ml-2 text-shoorah-secondary dark:text-white cursor-pointer"
                  onClick={() => setOpen(!open)}
                />
                {open && (
                  <Listbox onChange={(state) => handleOldNew(state)}>
                    {() => (
                      <>
                        <div className="relative">
                          <Transition
                            show={open}
                            as={Fragment}
                            leave="transition ease-in duration-100"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                          >
                            <Listbox.Options className="right-0 absolute shadow-lg text-center cursor-pointer z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                              {CONTENT_APPROVAL_TYPE.map((menu, index) => (
                                <Listbox.Option
                                  key={index}
                                  className="text-gray-900 relative cursor-default  py-2 pl-3 pr-9 hover:text-white hover:bg-shoorah-primary"
                                  disabled={menu?.value === ''}
                                  value={menu}
                                >
                                  <span className="font-normal cursor-pointer">{menu?.name}</span>
                                </Listbox.Option>
                              ))}
                            </Listbox.Options>
                          </Transition>
                        </div>
                      </>
                    )}
                  </Listbox>
                )}
              </div>
            ) : (
              <ViewChevronIcon
                className="w-[20px] ml-2 text-shoorah-secondary dark:text-white cursor-pointer"
                onClick={() => {
                  let uri = column.isTwoOption;
                  if (rowData?.contentType === 8 || name === 'survey_table_data') {
                    uri = getPathForSurvey(
                      `/view-survey/${
                        rowData?.contentType === 8 ? rowData?.content_type_id : rowData?.id
                      }?type=${name === 'content_approval' ? 'approval' : 'preview'}`
                    );
                  }
                  if (rowData?.contentType === 12) {
                    uri = `/self-assessment/manage/create?view=4&id=${rowData?.content_type_id}&contentApproval=1`;
                  }
                  navigate(uri, {
                    state: { ...rowData, action: 'view' }
                  });
                }}
              />
            ))}
          {column.isDelete && (
            <TrashIcon
              className="w-[20px] ml-2 text-red-500 cursor-pointer"
              onClick={() => {
                if (column.handleDelete) {
                  column.handleDelete(rowData);
                } else {
                  deleteHandler(rowData?.id || rowData?._id);
                }
              }}
            />
          )}
          {column.isResend && (
            <div
              className="text-shoorah-secondary dark:text-white cursor-pointer"
              onClick={() => {
                setSelectedRowOfTable(rowData);
                setOpenConfirmPopup(true);
              }}
            >
              Resend
            </div>
          )}
          {column?.isDownload && rowData?.download ? (
            <ArrowDownTrayIcon
              className="w-[20px] ml-2 text-shoorah-secondary dark:text-white cursor-pointer"
              onClick={() => {
                handleDownload(rowData?.download);
              }}
            />
          ) : column?.isDownload && !rowData?.download ? (
            'N/A'
          ) : (
            ''
          )}
          {column?.isReport && (
            <Popover className="">
              {({ open }) => (
                <>
                  <Popover.Button
                    className={`
                ${
                  open ? '' : 'text-opacity-90'
                } px-2 py-1 text-xs font-medium text-shoorah-secondary dark:text-white shadow-sm hover:shoorah-primary focus:outline-none sm:w-auto`}
                  >
                    <ArrowDownTrayIcon className="w-[20px] cursor-pointer" />
                  </Popover.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-200"
                    enterFrom="opacity-0 translate-y-1"
                    enterTo="opacity-100 translate-y-0"
                    leave="transition ease-in duration-150"
                    leaveFrom="opacity-100 translate-y-0"
                    leaveTo="opacity-0 translate-y-1"
                  >
                    <Popover.Panel className="bg-white rounded-md border absolute right-0 z-40 mt-1 max-w-[15rem] -translate-y-6 -translate-x-14 transform">
                      <p
                        className="px-4 py-2 hover:bg-gray-300 rounded-md cursor-pointer"
                        onClick={() => column?.isReport(rowData, 'CSV')}
                      >
                        Download CSV
                      </p>
                      <p
                        className="px-4 py-2 hover:bg-gray-300 rounded-md cursor-pointer"
                        onClick={() => column?.isReport(rowData, 'PDF')}
                      >
                        Download PDF
                      </p>
                    </Popover.Panel>
                  </Transition>
                </>
              )}
            </Popover>
          )}
        </div>
      );
    } else if (type === 'draftAction') {
      return (
        <div className="flex items-center justify-end">
          {column.isEdit && (
            <PencilSquareIcon
              className="w-[20px] ml-2 text-shoorah-secondary dark:text-white cursor-pointer"
              onClick={() => {
                navigate(column?.isEdit, {
                  state: { ...rowData, action: 'edit' }
                });
              }}
            />
          )}

          {column.isDelete && (
            <TrashIcon
              className="w-[20px] ml-2 text-red-500 cursor-pointer"
              onClick={() => deleteHandler(rowData?.id || rowData?._id)}
            />
          )}
          {column.isResend && (
            <div
              className="text-shoorah-secondary dark:text-white cursor-pointer"
              onClick={() => {
                setSelectedRowOfTable(rowData);
                setOpenConfirmPopup(true);
              }}
            >
              Resend
            </div>
          )}
          {column?.isDownload && (
            <ArrowDownTrayIcon
              className="w-[20px] ml-2 text-shoorah-secondary dark:text-white cursor-pointer"
              onClick={() => column?.isDownload(rowData)}
            />
          )}
          {column?.isReport && (
            <Popover className="">
              {({ open }) => (
                <>
                  <Popover.Button
                    className={`
                ${
                  open ? '' : 'text-opacity-90'
                } px-2 py-1 text-xs font-medium text-shoorah-secondary dark:text-white shadow-sm hover:shoorah-primary focus:outline-none sm:w-auto`}
                  >
                    <ArrowDownTrayIcon className="w-[20px] cursor-pointer" />
                  </Popover.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-200"
                    enterFrom="opacity-0 translate-y-1"
                    enterTo="opacity-100 translate-y-0"
                    leave="transition ease-in duration-150"
                    leaveFrom="opacity-100 translate-y-0"
                    leaveTo="opacity-0 translate-y-1"
                  >
                    <Popover.Panel className="bg-white rounded-md border absolute right-0 z-40 mt-1 max-w-[15rem] -translate-y-6 -translate-x-14 transform">
                      <p
                        className="px-4 py-2 hover:bg-gray-300 rounded-md cursor-pointer"
                        onClick={() => column?.isReport(rowData, 'CSV')}
                      >
                        Download CSV
                      </p>
                      <p
                        className="px-4 py-2 hover:bg-gray-300 rounded-md cursor-pointer"
                        onClick={() => column?.isReport(rowData, 'PDF')}
                      >
                        Download PDF
                      </p>
                    </Popover.Panel>
                  </Transition>
                </>
              )}
            </Popover>
          )}
        </div>
      );
    } else if (type === 'boolean') {
      return (
        <>
          <p
            className="w-[3rem] min-w-[2rem] inline-block overflow-hidden overflow-ellipsis text-center"
            id={column.key + rowData.id}
          >
            {data ? 'Yes' : 'No'}
          </p>
        </>
      );
    } else if (type === 'button') {
      return (
        <>
          <button class="hover:bg-blue-800 text-white font-light mr-1  px-8 rounded-3xl bg-shoorah-primary py-2">
            Pause
          </button>

          <button class="bg-red-600 hover:bg-red-800 text-white font-light  px-3 rounded-3xl py-2">
            Deactivate
          </button>
        </>
      );
    } else if (type === 'text') {
      return data ? (
        <>
          {column.longText ? (
            <p
              className="w-[20rem] min-w-[8rem] inline-block overflow-hidden overflow-ellipsis text-sm"
              id={column.key + rowData.id}
              data-tooltip-id={column.key + rowData.id}
              data-tooltip-content={data}
            >
              {data}
              {data?.length >= 35 && (
                <ReactTooltip
                  className="max-w-[350px] h-auto whitespace-normal "
                  id={column.key + rowData.id}
                  content={data}
                />
              )}
            </p>
          ) : (
            <p className="max-w-[500px] truncate text-sm" data-tooltip-id={column.key + rowData.id}>
              {data}
              {data?.length >= 35 && (
                <ReactTooltip
                  className="max-w-[350px] h-auto whitespace-normal "
                  id={column.key + rowData.id}
                  place="bottom"
                  content={data}
                />
              )}
            </p>
          )}
        </>
      ) : (
        'N/A'
      );
    } else if (type === 'text-without-black-hover') {
      return data ? (
        <>
          {column.longText ? (
            <p
              className="w-[20rem] min-w-[8rem] inline-block overflow-hidden overflow-ellipsis"
              id={column.key + rowData.id}
              data-tooltip-id={column.key + rowData.id}
              data-tooltip-content={data?.name}
            >
              {data}
              {/* {data?.length >= 35 && (
                <ReactTooltip
                  className="max-w-[350px] h-auto whitespace-normal "
                  id={column.key + rowData.id}
                  content={data}
                />
              )} */}
            </p>
          ) : (
            <p className="max-w-[500px] truncate" data-tooltip-id={column.key + rowData.id}>
              {data}
              {/* {data?.length >= 35 && (
                <ReactTooltip
                  className="max-w-[350px] h-auto whitespace-normal "
                  id={column.key + rowData.id}
                  place="bottom"
                  content={data}
                />
              )} */}
            </p>
          )}
        </>
      ) : (
        'N/A'
      );
    } else if (type === 'number') {
      return data ? (
        <>
          {column.longText ? (
            <p
              className="w-[20rem] min-w-[8rem] inline-block overflow-hidden overflow-ellipsis"
              id={column.key + rowData.id}
              data-tooltip-id={column.key + rowData.id}
              data-tooltip-content={data?.name}
            >
              {data}
              {data?.length >= 35 && <ReactTooltip id={column.key + rowData.id} />}
            </p>
          ) : (
            data
          )}
        </>
      ) : (
        '0'
      );
    } else if (type === 'Partner') {
      const rowDatas = {
        ...rowData,
        id: rowData?.partnerId
      };
      return data ? (
        <button
          className="hover:text-blue-900 underline"
          onClick={() =>
            navigate(column?.isEdit, {
              state: {
                ...rowDatas,
                action: 'edit'
              }
            })
          }
        >
          {data}
        </button>
      ) : (
        'N/A'
      );
    } else if (type === 'text2') {
      return data ? (
        <>
          {column.longText ? (
            <p
              onClick={() => navigate(`/peap-management/profile/${rowData?.id}`)}
              className={`w-[8rem] !underline min-w-[8rem] inline-block overflow-hidden overflow-ellipsis ${
                column.align === 'left'
                  ? 'text-left'
                  : column.align === 'right'
                  ? 'text-right'
                  : 'text-center'
              }`}
              id={column.key + rowData.id}
              data-tooltip-id={column.key + rowData.id}
              data-tooltip-content={data?.name}
            >
              {data}
              {data?.length >= 35 && <ReactTooltip id={column.key + rowData.id} />}
            </p>
          ) : (
            data
          )}
        </>
      ) : (
        '━━'
      );
    } else if (type === 'text3') {
      return data ? (
        <>
          {column.longText ? (
            <p
              onClick={onNameClick}
              className={`w-[10rem] !underline min-w-[8rem] inline-block overflow-hidden overflow-ellipsis ${
                column.align === 'left'
                  ? 'text-left'
                  : column.align === 'right'
                  ? 'text-right'
                  : 'text-center'
              }`}
              id={column.key + rowData.id}
              data-tooltip-id={column.key + rowData.id}
              data-tooltip-content={data?.name}
            >
              {data}
              {data?.length >= 35 && <ReactTooltip id={column.key + rowData.id} />}
            </p>
          ) : (
            data
          )}
        </>
      ) : (
        'N/A'
      );
    } else if (type === 'views') {
      return data ? (
        <p
          className=" min-w-[3rem] flex flex-col rounded-xl justify-center items-center  text-white py-1 overflow-hidden bg-shoorah-primary overflow-ellipsis"
          id={column.key + rowData.id}
          onMouseEnter={(e) =>
            (e.currentTarget.querySelector('span').style.display = 'inline-block')
          }
          onMouseLeave={(e) => (e.currentTarget.querySelector('span').style.display = 'none')}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="w-6 h-6"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
            />
          </svg>
          <span className="hidden cursor-default"> {data}</span>
        </p>
      ) : (
        0
      );
    } else if (type === 'name-email') {
      return (
        <div className="flex flex-col min-w-[180px]">
          <div className="font-medium dark:text-white text-gray-900 truncate">
            {rowData?.recipient_name || 'N/A'}
          </div>
          <div className="text-gray-500 lowercase text-sm truncate">
            {rowData?.recipient_email || 'N/A'}
          </div>
        </div>
      );
    } else if (type === 'sendBy') {
      return data ? (
        <>
          {column.longText ? (
            <p
              className="w-[20rem] min-w-[8rem] inline-block overflow-hidden overflow-ellipsis"
              id={column.key + rowData.id}
              data-tooltip-id={column.key + rowData.id}
              data-tooltip-content={data?.name}
            >
              {data?.name}
              {data?.name.length >= 35 && <ReactTooltip id={column.key + rowData.id} />}
            </p>
          ) : (
            data?.name
          )}
        </>
      ) : (
        'N/A'
      );
    } else if (type === 'date') {
      return (
        <p className="text-sm font-normal text-black">
          {data ? moment(data).format('MMM D, YYYY') : 'N/A'}
        </p>
      );
    } else if (type === 'image') {
      return (
        <LazyLoadImageProp
          imageSrc={data ? data : Thumbnail}
          className="w-full max-w-[70px] h-auto rounded-md"
        />
      );
    } else if (type === 'array') {
      const array = data.slice(0, 2);
      return (
        <div>
          {array?.map((value, key) => (
            <div key={key}>
              {key === array.length - 1 ? value?.display_name : value?.display_name + ','}
            </div>
          ))}
          {data?.length > 2 ? (
            <div
              className="text-blue-800 font-medium cursor-pointer"
              onClick={() => focusPopUpHandler(data)}
            >
              +{data?.length - array.length} more
            </div>
          ) : null}
        </div>
      );
    } else if (type === 'play') {
      return (
        <>
          <div className="flex items-start justify-center ">
            <AudioPlayer
              onPlay={() => activePlayerHandler(rowData.id || rowData._id)}
              pauseHandler={pauseHandler}
              playing={activePlaying === rowData.id || activePlaying === rowData._id}
              audioPath={rowData[column.key]}
            />
          </div>
        </>
      );
    } else if (type === 'status') {
      return data ? (
        <button
          className={` rounded-full w-[216px] py-1 px-4  ${column.style?.opacity} ${
            column.style?.align
          } ${column.style?.textColor} ${getStatusBgColorPeapExpert(data)} text-sm font-normal`}
        >
          {data}
        </button>
      ) : (
        'N/A'
      );
    }

    if (type === 'notificationAudio') {
      return (
        <>
          {rowData.audioUrl ? (
            <div className="flex items-start justify-center">
              <AudioPlayer
                onPlay={() => activePlayerHandler(rowData.id)}
                pauseHandler={pauseHandler}
                playing={activePlaying === rowData.id}
                audioPath={rowData[column.key]}
              />
            </div>
          ) : (
            <p className="text-center">N/A</p>
          )}
        </>
      );
    } else if (type === 'statusChip') {
      /**
       * A chip, not a toggle: the column reports what the account is, it does
       * not change it. Enabling and disabling now happens on the record
       * itself, where the admin can see who they are acting on — a switch in
       * a dense list is one mis-click away from locking out the wrong person.
       */
      const raw = rowData[column.key];
      // A Norwich fan with no current season ticket: the account still works,
      // on Shoorah basic, and the club is no longer invoiced for it.
      if (raw === 'Lapsed') {
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-[#FFF4E5] text-[#B26A00] dark:bg-[#2A241A] dark:text-[#F0B35A]">
            Lapsed
          </span>
        );
      }
      // Users sends 'Active'/'Inactive', experts send a boolean, and some
      // rows predate the field entirely — those are active by default,
      // matching what the API projects.
      const active = raw === 'Active' || raw === 1 || raw === true || raw == null;
      return (
        <span
          className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${
            active
              ? 'bg-[#EDF9EE] text-[#2E7D32] dark:bg-[#1E2A1F] dark:text-[#7BC47F]'
              : 'bg-[#FDEFEF] text-[#C0392B] dark:bg-[#2A1E1E] dark:text-[#E88A80]'
          }`}
        >
          {active ? 'Active' : 'Disabled'}
        </span>
      );
    } else if (type === 'toggle') {
      return (
        <>
          <ActiveInactiveToggle
            accountStatus={rowData[column.key]}
            userId={rowData.id ? rowData.id : rowData._id}
          />
        </>
      );
    } else if (type === 'company-toggle') {
      return (
        <>
          <ActiveInactiveCompanyToggle
            accountStatus={rowData[column.key]}
            userId={rowData.id ? rowData.id : rowData._id}
          />
        </>
      );
    } else if (type === 'verified') {
      return (
        <p
          className={`px-6 py-1 text-xs inline-block capitalize rounded-full border text-center ${
            data === false || data === null
              ? 'bg-[#F9F2F5] border-[#BF3261] text-[#BF3261]'
              : 'bg-[#DDF2BF] border-[#438038] text-[#438038]'
          }`}
        >
          {data === false || data === null ? 'Pending' : 'Verified'}
        </p>
      );
    } else if (type === 'platform' || type === 'purchase_platform') {
      return (
        <div className="w-full flex justify-center relative">
          {
            <img
              loading="lazy"
              src={
                data === 0
                  ? mailIcon
                  : data === 1 && type === 'purchase_platform'
                  ? googleIcon
                  : data === 1 && type === 'platform'
                  ? appleIcon
                  : data === 2 && type === 'purchase_platform'
                  ? appleIcon
                  : data === 2 && type === 'platform'
                  ? googleIcon
                  : fbIcon
              }
              className="h-6 w-6 cursor-pointer"
              id={type === 'purchase_platform' ? rowData.transactionId : rowData.id}
              alt="platform"
            />
          }
          <ReactTooltip
            anchorId={type === 'purchase_platform' ? rowData.transactionId : rowData.id}
            place="left"
            content={
              data === 0
                ? 'Shoorah'
                : data === 1 && type === 'purchase_platform'
                ? 'Google'
                : data === 1 && type === 'platform'
                ? 'Apple'
                : data === 2 && type === 'purchase_platform'
                ? 'Apple'
                : data === 2 && type === 'platform'
                ? 'Google'
                : 'Facebook'
            }
            className="bg-shoorah-secondary"
          />
        </div>
      );
    } else if (type === 'rating') {
      return (
        <div className="star-rating">
          {[...Array(5)].map((star, index) => {
            index += 1;
            return (
              <button
                type="button"
                key={index}
                disabled
                className={index <= rowData.rating ? 'text-shoorah-primary' : 'text-white'}
              >
                <span className="star">&#9733;</span>
              </button>
            );
          })}
        </div>
      );
    } else if (type === 'profile1') {
      return (
        <div className="flex items-center">
          <div className="flex-shrink-0"></div>
          <div className="">
            <div className="font-medium dark:text-white text-black no-underline decoration-none">
              {rowData.name}
            </div>

            <div className="text-[10px] pt-[5px] no-underline font-medium dark:text-white text-black lowercase">
              {rowData?.email}
            </div>
          </div>
        </div>
      );
    } else if (type === 'Notebutton') {
      return (
        <>
          <button class="bg-[#F1F1F1] hover:bg-red-700 text-[14px] text-gray-900 font-semibold  py-2 px-5 rounded-lg ">
            NotesDone
          </button>
        </>
      );
    } else if (type === 'Notepush') {
      return (
        <>
          <button class=" text-[14px] text-black font-semibold   border-none focus:outline-none">
            Send push to book session
          </button>
        </>
      );
    } else if (type === 'viewneditpush') {
      return (
        <div class="flex">
          {/* <img src={Notebook} alt="NotebookImage" className=" w-[25px] h-auto " /> */}
          <button class=" pl-[12px] text-[12px] text-black font-semibold   border-none focus:outline-none">
            View & edit notes
          </button>
        </div>
      );
    } else if (type === 'feedback') {
      return rowData.feedback?.[column.key];
    } else if (type === 'customTemplate') {
      return column.customTemplateRender(rowData);
    } else if (type === 'dropdown') {
      return (
        <div className="w-[150px]">
          <Select
            options={options}
            onChange={handleDropdownChange}
            value={rowData?.selectedOption}
          />
        </div>
      );
    }
  };

  const handleDeletePopup = () => {
    //console.log("deleteId 1005", idToDelete);
    setDeletePopup(false);
    setDeleteId(idToDelete);
    setIdToDelete('');
  };

  const handleBulkOperation = () => {
    setBulkOperationPopup(false);
    setSearchTerm('');
    if (name === 'users_table') {
      const payload = {
        userIds: selectedData,
        userStatus: actionType
      };
      Api.postUserBulkAction(payload).then((response) => {
        if (response.data.meta.code === 1) {
          successToast(response.data.meta.message);
          setSelectedData([]);
          setChecked(false);
          setIndeterminate(false);
          refreshTable();
        } else if (response.data.meta.code === 0) {
          errorToast(response.data.meta.message);
        }
      });
    } else {
      const payload = {
        contentIds: selectedData,
        contentType: contentType,
        contentStatus: actionType
      };
      //api call to delete
      Api.postBulkAction(payload).then((response) => {
        if (response.data.meta.code === 1) {
          successToast(response.data.meta.message);
          setSelectedData([]);
          setChecked(false);
          setIndeterminate(false);
          refreshTable();
        } else if (response.data.meta.code === 0) {
          errorToast(response.data.meta.message);
        }
      });
    }
  };

  const handleResendNotification = (e, row) => {
    e.preventDefault();
    setSelectedRow(row);
    setOpenConfirmPopup(false);
  };

  return (
    <>
      <div>
        <div className={tbodyMaxHeight ? `overflow-auto ${tbodyMaxHeight} ` : 'overflow-x-auto'}>
          <div className="inline-block min-w-full align-middle">
            <div
              style={{ minHeight: minHeight ? minHeight : '' }}
              className={`overflow-visible border-[1.5px] dark:border-shoorah-darkBorder
                 border-shoorah-borderGray 
                rounded-[10px] min-h-${minHeight} dark:bg-shoorah-darkBgTabColor bg-white`}
            >
              {data?.length > 0 ? (
                <table className="relative min-w-full border-separate border-spacing-0 text-shoorah-lightBlack">
                  <thead className="dark:bg-shoorah-darkBgTabColor bg-[#FBFBFB] [&>tr>th:first-child]:rounded-tl-[9px] [&>tr>th:last-child]:rounded-tr-[9px] [&>tr>th]:text-shoorah-gray3 [&>tr>th]:dark:text-shoorah-gray3">
                    <tr>
                      {showIndex && (
                        <th className="text-centre p-3 text-left font-medium whitespace-nowrap py-[18px] bg-shoorah-tableHeader dark:bg-shoorah-darkBgTabColor pl-[26px] opacity-1 dark:text-white dark:opacity-1">
                          Serial No.
                        </th>
                      )}
                      {columns?.map((column, index) => {
                        const isColumnHighlighted =
                          column.sortable && sortBy && sortBy === column?.sortKey;
                        const isColumnFreezableByConfig = isColumnFreezable(column);
                        const freezeId = getColumnFreezeId(column, index);
                        const isColumnPinned = Boolean(frozenColumnsMap[freezeId]);
                        const widthClasses = column.width
                          ? `max-w-[${column.width}px] min-w-[20px] text-ellipsis overflow-hidden whitespace-nowrap`
                          : '';
                        const minusWidthClasses = column.width
                          ? `max-w-[${Math.max(
                              column.width - 20,
                              20
                            )}px] text-ellipsis overflow-hidden whitespace-nowrap`
                          : '';
                        const lastIndex = columns.length - 1;
                        return (
                          <Fragment key={index}>
                            {column.type === 'checkBox1' ? (
                              <th
                                scope="col"
                                className={`bg-shoorah-tableHeader dark:bg-shoorah-darkBgTabColor relative w-12 px-6 sm:w-16 sm:px-8 ${
                                  index === 0 ? 'pl-[26px]' : ''
                                } ${lastIndex === index ? 'pr-[38px]' : ''} ${widthClasses} ${
                                  column.className
                                }`}
                              >
                                <input
                                  type="checkbox1"
                                  className={`${
                                    data?.length !== 0 && 'cursor-pointer'
                                  } absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border border-[blue] accent-shoorah-primary sm:left-6`}
                                  ref={checkbox}
                                  checked={checked}
                                  onChange={toggleAll}
                                  disabled={data?.length === 0}
                                />
                              </th>
                            ) : (
                              <th
                                key={index}
                                scope="col"
                                style={getStickyStyle(index, true)}
                                className={`p-3 py-[18px] bg-shoorah-tableHeader dark:bg-shoorah-darkBgTabColor ${
                                  column.sortable ? 'cursor-pointer' : ''
                                } ${
                                  columns[0]?.type === 'checkBox' &&
                                  index === 1 &&
                                  column.type !== 'play'
                                    ? 'min-w-[14rem]'
                                    : ''
                                } ${
                                  column.style?.align ||
                                  (column.align === 'left'
                                    ? 'text-left'
                                    : column.align === 'center'
                                    ? 'text-center'
                                    : 'text-right')
                                } ${column.extend ? 'min-w-[13rem]' : ''} ${
                                  index === 0 ? 'pl-[26px]' : ''
                                } ${lastIndex === index ? 'pr-[38px]' : ''} font-medium ${
                                  isColumnHighlighted
                                    ? 'text-shoorah-darkPurple dark:text-white'
                                    : 'dark:text-white'
                                } ${widthClasses} ${column.className} whitespace-nowrap`}
                              >
                                {column.sortable ? (
                                  <Fragment>
                                    <ReactTooltip id={`table-col-${index}-tip`} />
                                    <div
                                      data-tooltip-id={`table-col-${index}-tip`}
                                      data-tooltip-content={column.title}
                                      className="flex items-center gap-1"
                                      onClick={() =>
                                        setSortBy(column?.sortKey ? column?.sortKey : column.key)
                                      }
                                    >
                                      <span className={`self-center ${minusWidthClasses}`}>
                                        {column.title}{' '}
                                      </span>
                                      <span className="self-center">
                                        {column.sortable ? (
                                          sortBy && sortOrder && sortBy === column?.sortKey ? (
                                            sortOrder === 1 ? (
                                              <ChevronDoubleUpIcon className="ml-2 w-4" />
                                            ) : (
                                              <ChevronDoubleDownIcon className="ml-2 w-4" />
                                            )
                                          ) : (
                                            <ChevronUpDownIcon className="w-[20px]" />
                                          )
                                        ) : (
                                          ''
                                        )}
                                      </span>
                                      {isColumnFreezableByConfig && (
                                        <button
                                          type="button"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            toggleColumnFreeze(column, index);
                                          }}
                                          className="self-center text-shoorah-darkPurple dark:text-white"
                                          title={
                                            isColumnPinned ? 'Unfreeze column' : 'Freeze column'
                                          }
                                          aria-label={
                                            isColumnPinned ? 'Unfreeze column' : 'Freeze column'
                                          }
                                        >
                                          {isColumnPinned ? (
                                            <BookmarkSolidIcon className="h-4 w-4" />
                                          ) : (
                                            <BookmarkOutlineIcon className="h-4 w-4" />
                                          )}
                                        </button>
                                      )}
                                    </div>
                                  </Fragment>
                                ) : (
                                  <div className="flex items-center gap-1">
                                    <span>{column.title}</span>
                                    {isColumnFreezableByConfig && (
                                      <button
                                        type="button"
                                        onClick={() => toggleColumnFreeze(column, index)}
                                        className="self-center text-shoorah-darkPurple dark:text-white"
                                        title={isColumnPinned ? 'Unfreeze column' : 'Freeze column'}
                                        aria-label={
                                          isColumnPinned ? 'Unfreeze column' : 'Freeze column'
                                        }
                                      >
                                        {isColumnPinned ? (
                                          <BookmarkSolidIcon className="h-4 w-4" />
                                        ) : (
                                          <BookmarkOutlineIcon className="h-4 w-4" />
                                        )}
                                      </button>
                                    )}
                                  </div>
                                )}
                              </th>
                            )}
                          </Fragment>
                        );
                      })}
                    </tr>
                  </thead>
                  {/* divide-y */}
                  <tbody
                    className={`dark:bg-shoorah-darkBgTabColor bg-white text-sm [&>tr:not(:last-child)>td]:border-b [&>tr>td]:border-shoorah-borderGray dark:[&>tr>td]:border-shoorah-darkBorder`}
                  >
                    {loader ? (
                      <tr>
                        <td>
                          <Loader />
                        </td>
                      </tr>
                    ) : (
                      <>
                        {data?.length > 0 ? (
                          data?.map((data, index, arr) => {
                            const lastRow = index === arr.length - 1;
                            const isSecondHighlight = columns.some((item) =>
                              ['checkBox', 'play'].includes(item.type)
                            );
                            const firstColIndex = isSecondHighlight ? 1 : 0;
                            /**
                             * The "currently open" row.
                             *
                             * This used to be two `data?.x === activeRowId &&`
                             * expressions. Where a table has no active row,
                             * `activeRowId` is undefined — and so is `_id` on
                             * any list whose API projects `id` instead (the
                             * experts endpoint does exactly that). Comparing
                             * two undefineds is true, so every row got the
                             * highlight and the table rendered grey.
                             *
                             * The null check is the fix: no active row means
                             * no highlighted row.
                             */
                            const isActiveRow =
                              activeRowId != null &&
                              (data?._id === activeRowId || data?.id === activeRowId);
                            return (
                              <tr
                                key={index}
                                className={`${
                                  data?.status === 'winner'
                                    ? isActiveRow
                                      ? 'bg-[#EEE]'
                                      : 'bg-green-100'
                                    : ''
                                } ${
                                  isActiveRow ? 'bg-[#EEEEEE] dark:bg-shoorah-darkBgColor' : ''
                                } hover:bg-[#EEEEEE] dark:hover:bg-shoorah-darkBgColor md:cursor-pointer`}
                              >
                                {showIndex && (
                                  <td
                                    className={`px-3 py-4 pl-[26px] dark:text-white whitespace-nowrap ${
                                      index === 0 ? 'mt-3' : ''
                                    }`}
                                  >
                                    {index + 1}.
                                  </td>
                                )}
                                {columns?.map((column, index1) => {
                                  const hasAnyDarkTextColumn = columns.some(
                                    (column) => column.darkText
                                  );
                                  const widthClasses = column.width
                                    ? `max-w-[${column.width}px] min-w-[20px] text-ellipsis overflow-hidden whitespace-nowrap`
                                    : '';

                                  const lastIndex = columns.length - 1 === index1;
                                  return (
                                    <Fragment key={index1}>
                                      {column.type === 'checkBox' ? (
                                        <td
                                          style={getStickyStyle(index1)}
                                          className={`relative px-3 py-4 flex justify-center items-center ${
                                            index1 === 0 ? 'pl-[26px]' : ''
                                          } ${lastRow ? 'pb-[24px]' : ''} ${widthClasses} ${
                                            column.rowClassName
                                          }`}
                                        >
                                          <input
                                            type="checkbox"
                                            className="cursor-pointer block shrink-0 h-4 w-4 rounded border-gray-300 accent-blue-200 "
                                            value={data.id}
                                            checked={selectedData.includes(data.id)}
                                            onChange={(e) =>
                                              setSelectedData(
                                                e.target.checked
                                                  ? [...selectedData, data.id]
                                                  : selectedData.filter((p) => p !== data.id)
                                              )
                                            }
                                          />
                                        </td>
                                      ) : column.type === 'companyName' ? (
                                        <td
                                          style={getStickyStyle(index1)}
                                          className={`test1 whitespace-nowrap ${
                                            column.style?.align ||
                                            (column.align === 'left'
                                              ? 'text-left'
                                              : column.align === 'center'
                                              ? 'text-center'
                                              : 'text-right')
                                          } px-3 py-4 text-sm dark:text-white ${
                                            lastRow ? 'pb-[26px]' : ''
                                          } ${index1 === 0 ? 'pl-[26px]' : ''} ${
                                            column.transform ? column.transform : ''
                                          } ${widthClasses} ${column.rowClassName || ''} ${
                                            lastIndex ? 'pr-[38px]' : ''
                                          } ${
                                            firstColIndex === index1 ? 'font-medium' : ''
                                          }  ${
                                            column.key !== 'action' ? 'max-w-[500px] truncate' : ''
                                          }`}
                                        >
                                          <div className="flex flex-row items-center gap-2">
                                            <p className="whitespace-nowrap">
                                              {formattedData(
                                                data,
                                                data[column.key1],
                                                column.type,
                                                column,
                                                index1,
                                                column?.parant,
                                                column?.parantURL
                                              )}
                                            </p>
                                          </div>
                                        </td>
                                      ) : (
                                        <td
                                          key={index}
                                          style={getStickyStyle(index1)}
                                          className={`${
                                            column.style?.align ||
                                            (column.align === 'left'
                                              ? 'text-left'
                                              : column.align === 'center'
                                              ? 'text-center'
                                              : 'text-right')
                                          }
                                          px-3 py-4 dark:text-white ${lastRow ? 'pb-[24px]' : ''} ${
                                            index1 === 0 ? 'pl-[26px]' : ''
                                          }
                                          ${column.transform || ''}
                                          ${
                                            column.darkText ||
                                            (!hasAnyDarkTextColumn && index1 === 0)
                                              ? ''
                                              : column.style?.opacity || ''
                                          } ${lastIndex ? 'pr-[38px]' : ''}
                                          ${widthClasses} ${column.rowClassName || ''} ${
                                            firstColIndex === index1 ? 'font-medium' : ''
                                          } ${
                                            column?.key !== 'action' ? 'max-w-[500px] truncate' : ''
                                          }`}
                                        >
                                          {column?.nested
                                            ? formattedData(
                                                data,
                                                data[column.key1]?.[column.key2],
                                                column.type,
                                                column,
                                                index1
                                              )
                                            : formattedData(
                                                data,
                                                data[column.key],
                                                column.type,
                                                column,
                                                index1,
                                                column?.parant,
                                                column?.parantURL
                                              )}
                                        </td>
                                      )}
                                    </Fragment>
                                  );
                                })}
                              </tr>
                            );
                          })
                        ) : (
                          <tr>
                            <td colSpan={columns.length}>
                              <div className="py-[20px] bg-white border-t border-[#EAEAEA] w-full">
                                <NoDataFoundImg
                                  className={`m-auto text-indigo-50 border border-shoorah-blue rounded-lg`}
                                />
                                <p className="text-center text-shoorah-gray4 text-sm mt-3">
                                  No results
                                </p>
                                {addNewURL && (
                                  <div className="text-center mt-3">
                                    <button
                                      type="button"
                                      onClick={() => navigate(addNewURL)}
                                      className="btn-primary sm:w-auto"
                                    >
                                      Add New
                                    </button>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    )}
                  </tbody>
                </table>
              ) : (
                <div className="h-full py-8 dark:bg-shoorah-darkBgTabColor bg-white rounded-xl border-[#EAEAEA] w-full">
                  <NoDataFoundImg
                    className={`m-auto text-indigo-50 border dark:border-shoorah-darkBorder border-shoorah-blue rounded-lg`}
                  />
                  <p className="text-center text-shoorah-gray4 text-sm mt-3">No results</p>
                  {addNewURL && (
                    <div className="text-center mt-3">
                      <button
                        type="button"
                        onClick={() => navigate(addNewURL)}
                        className="btn-primary sm:w-auto"
                      >
                        Add New
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {openConfirmPopup && (
        <ConfirmPopup
          open={openConfirmPopup}
          setOpen={setOpenConfirmPopup}
          message={'Are you sure you want to resend notification?'}
          setAccepted={(e) => handleResendNotification(e, selectedRowOfTable)}
        />
      )}
      {openResetPopup && (
        <CommonModal
          open={openResetPopup}
          onPrimaryClick={() => {
            console.log(selectedRowOfTable);
            handleResetPassword(selectedRowOfTable.id);
            setOpenResetPopup(false);
          }}
          onSecondaryClick={() => setOpenResetPopup(false)}
          title="Reset Password"
          message="Are you sure you want to reset the password?"
          primaryButtonText="Reset"
          secondaryButtonText="Cancel"
        />
      )}
      {openDeletePopup && (
        <DeletePopup
          open={openDeletePopup}
          setOpen={setDeletePopup}
          setDelete={handleDeletePopup}
        />
      )}
      {bulkOperationPopup && (
        <BulkOperationPopup
          open={bulkOperationPopup}
          setOpen={setBulkOperationPopup}
          setDelete={handleBulkOperation}
          actionType={actionType}
        />
      )}
      <FocusPopup open={openFocusPopup} setOpen={setOpenFocusPopup} data={focusData} />
      {openVideoPlayer && <VideoPlayer open={openVideoPlayer} setOpen={setVideoPlayer} />}
    </>
  );
};

Table.propTypes = {
  columns: PropTypes.any,
  data: PropTypes.any,
  contentType: PropTypes.any,
  setDeleteId: PropTypes.func,
  setSelectedRow: PropTypes.func,
  setSortBy: PropTypes.func,
  refreshTable: PropTypes.func,
  name: PropTypes.string,
  addNewURL: PropTypes.string,
  bottomBorder: PropTypes.bool,
  loader: PropTypes.bool,
  setSearchTerm: PropTypes.func,
  showIndex: PropTypes.bool,
  handleDropdownChange: PropTypes.func,
  options: PropTypes.array
};

export default Table;
